import { useState } from 'react'
import { useQuery } from '@apollo/client'
import { GET_PRODUCTS } from '../../graphql/product-queries'

/**
 * Pick the products a reward rung applies to.
 *
 * Returns PRODUCT ids, never variant ids: the API checks a rung's
 * `params_json.product_ids` against the `Product` table. Ids are plain
 * integers here — unlike the loyalty programme's own ids, which are strings.
 *
 * Touchscreen: the whole row is the tap target and the selection count is
 * printed under the list; nothing is explained by hover alone.
 */
interface ProductPickerProps {
  selectedIds: number[]
  onChange: (ids: number[]) => void
}

interface PickerProduct {
  id: number
  name: string
  active: boolean
}

const ProductPicker = ({ selectedIds, onChange }: ProductPickerProps) => {
  const [search, setSearch] = useState('')
  const { data, loading } = useQuery(GET_PRODUCTS)

  // errorPolicy is 'all' by default, so data can be undefined on a failure —
  // the global errorLink has already toasted it. Inactive products are hidden
  // UNLESS a rung already names them: a selection the operator cannot see
  // cannot be removed either, and would be resent on every save.
  const products: PickerProduct[] = (data?.products ?? []).filter(
    (p: PickerProduct) => p.active || selectedIds.includes(p.id)
  )

  const filtered = products
    .filter((p) => p.name.toLowerCase().includes(search.trim().toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))

  const toggle = (id: number) => {
    onChange(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id])
  }

  return (
    <div className="space-y-2">
      <input
        type="text"
        className="input-field text-sm"
        placeholder="Search products…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      {loading && products.length === 0 && <p className="text-sm text-gray-400">Loading…</p>}
      <div className="max-h-56 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
        {filtered.map((product) => {
          const checked = selectedIds.includes(product.id)
          return (
            <label
              key={product.id}
              className={`flex items-center gap-3 px-3 py-2 cursor-pointer ${
                checked ? 'bg-primary-50' : 'hover:bg-gray-50'
              }`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(product.id)}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span
                className={`text-sm ${checked ? 'font-medium text-primary-900' : 'text-gray-700'}`}
              >
                {product.name}
                {!product.active && <span className="ml-2 text-xs text-gray-400">(inactive)</span>}
              </span>
            </label>
          )
        })}
        {!loading && filtered.length === 0 && (
          <p className="px-3 py-2 text-sm text-gray-400">No products found.</p>
        )}
      </div>
      <p className="text-xs text-gray-500">
        {selectedIds.length === 0
          ? 'No products selected.'
          : `${selectedIds.length} product${selectedIds.length === 1 ? '' : 's'} selected`}
      </p>
    </div>
  )
}

export default ProductPicker
